import { useEffect } from "react";

// The portal's loading bar (NewComponents/LoadingBar): a thin bar moving left
// to right along the top of the window while a screen or its data loads —
// the same bar the last visit's copy carries along its top edge
// (Hooks/useLastVisit). This is only the count of what is loading:
//   const stop = startLoading();  ...  stop();   (each stop once; extra calls do nothing)
//   usePageLoading(loading)                       (while `loading` is true)
// Who starts it: a list until its rows show (utility/listGate holdForList),
// a page until this visit's data is in (useLastVisit), a screen's file
// downloading (lazyPage's fallback), sign-in (PortalSkeleton) and the page
// loaders (Loader, NoData loading).
// While the last visit's copy is on screen it has its own bar, so the top
// one stays hidden (showLastVisitCopy), never two at once.
// Nothing loads for ever: each start ends by itself after MAX_MS.

const MAX_MS = 15000;

let loading = 0;
let copies = 0;
const listeners = new Set();

const emit = () => listeners.forEach((fn) => {
  try {
    fn();
  } catch (e) {
    // a listener that went away
  }
});

const counter = (add) => {
  add(1);
  emit();
  let done = false;
  const timer = setTimeout(() => stop(), MAX_MS);
  const stop = () => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    add(-1);
    emit();
  };
  return stop;
};

export const startLoading = () => counter((n) => {
  loading = Math.max(0, loading + n);
});

// The last visit's copy is on screen (with its own bar along its top).
export const showLastVisitCopy = () => counter((n) => {
  copies = Math.max(0, copies + n);
});

// { loading, copy }: something is loading; a copy with its own bar is up.
export const loadingState = () => ({ loading: loading > 0, copy: copies > 0 });

export const subscribeLoading = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const usePageLoading = (on) => {
  useEffect(() => (on ? startLoading() : undefined), [on]);
};
